import { useParams } from 'react-router';

import {
  useGetEcommerceOrder,
  useGetPayLinkByPayLinkId,
} from '../../../api/lending/lapiHooks';
import { LinkWithQuery } from '../../../components/LinkWithQuery/LinkWithQuery';
import { LoadingScreen } from '../../../components/LoadingScreen/LoadingScreen';
import Chevron from '../../../components/svg/ChevronLeft.svg';
import IwocaPayIcon from '../../../components/svg/IwocaPayIconSmall.svg';
import { useSellerWhitelabelling } from '../../../hooks/useSellerWhiteLabelling.hook';
import { OrderLinkIsNotValid } from '../../../Pages/FailurePages/OrderLinkIsNotValid';
import { OrderNotFound } from '../../../Pages/FailurePages/OrderNotFound';
import { useThreatMetrixProfiling } from '../../../utils/threatMetrix/ThreatMetrixProfiling';
import { WhiteLabelLandingLogo } from '../../components/WhiteLabelLogo/WhiteLabelLogo';
import { WhiteLabelOverride } from '../../components/WhiteLabelOverride/WhiteLabelOverride';
import BenefitIwocaPay from '../assets/BenefitIwocaPay.svg';
import BenefitPencil from '../assets/BenefitPencil.svg';
import BenefitPieChart from '../assets/BenefitPieChart.svg';
import OrderLandingPageGraphic from '../assets/OrderLandingPageGraphic.jpg';

const OrderLandingPage = () => {
  const { orderId } = useParams();

  const { ecommerceOrder, loadingEcommerceOrder } = useGetEcommerceOrder({
    orderId,
  });

  // White-label theme.
  const { payLink } = useGetPayLinkByPayLinkId({
    payLinkId: ecommerceOrder?.pay_link_id,
  });
  const { theme, showSellerLogo, loadingWhitelabelling } =
    useSellerWhitelabelling(payLink?.seller_handle);

  useThreatMetrixProfiling(payLink?.seller_handle, payLink?.id);

  if (loadingEcommerceOrder || (!!ecommerceOrder && loadingWhitelabelling)) {
    return <LoadingScreen />;
  }

  if (!ecommerceOrder) {
    return <OrderNotFound />;
  }

  if (ecommerceOrder.status !== 'CREATED') {
    return <OrderLinkIsNotValid />;
  }

  return (
    <div className="min-h-screen bg-secondary-97">
      <div className="mx-auto py-[1.5rem] pb-[3rem] text-left m600:max-w-[480px] m600:py-[2rem] m600:pb-[3.75rem]">
        <a
          className="mb-[1.25rem] inline-flex items-center text-center text-[0.875rem] font-bold leading-[1.25rem] text-denim-35 no-underline"
          href={ecommerceOrder.redirect_url}
        >
          <span className="mr-[0.25rem] inline-flex fill-denim-35">
            <img src={Chevron} alt="" />
          </span>
          Return to {ecommerceOrder.seller_trading_name}
        </a>

        <div className="overflow-hidden rounded-s border border-london-90 bg-white shadow-order-card">
          {theme && showSellerLogo ? (
            <WhiteLabelLandingLogo
              url={theme.logo_uri!}
              ratio={theme.preferred_logo_type!}
              themeColor={theme.primary_color}
            />
          ) : (
            <img
              className="block w-full"
              src={OrderLandingPageGraphic}
              alt="Landing page graphic"
            />
          )}
          <div className="mx-[0.75rem] my-[0.75rem] mb-[1.25rem] m600:m-[1.5rem]">
            <div className="mb-[0.75rem] m600:mb-[1.5rem]">
              <h1 className="m-0 mb-[0.5rem] text-[1.5rem] font-bold leading-[2rem] m600:text-[2rem] m600:leading-[2.75rem]">
                Pay later for businesses
              </h1>
              <p className="m-0 flex items-center text-left text-xs leading-[1rem] text-grey-60">
                Powered by
                <LinkWithQuery
                  to="/iwocapay-sellers/"
                  className="inline-flex"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <img
                    className="ml-[0.5rem] mt-[0.2rem]"
                    src={IwocaPayIcon}
                    alt="iwocaPay logo"
                  />
                </LinkWithQuery>
              </p>
            </div>
            <ul className="m-0 list-none p-0">
              <li className="mb-[0.75rem] flex m600:mb-[1.5rem]">
                <span className="mr-[0.75rem] hidden overflow-visible m600:block">
                  <img src={BenefitPieChart} alt="" />
                </span>
                <div>
                  <h2 className="m-0 mb-[0.25rem] text-m font-bold leading-[1.5rem] m600:mb-[0.5rem]">
                    Spread the cost
                    {ecommerceOrder.pricing.representative_interest === 0 &&
                      ' interest-free'}
                  </h2>
                  <p className="m-0 text-s font-reg leading-[1.25rem] m600:text-m m600:leading-[1.5rem]">
                    Equal monthly payments taken automatically &mdash; the first
                    one is taken 30 days after you’ve checked out.
                  </p>
                </div>
              </li>
              <li className="mb-[0.75rem] flex m600:mb-[1.5rem]">
                <span className="mr-[0.75rem] hidden overflow-visible m600:block">
                  <img src={BenefitIwocaPay} alt="" />
                </span>
                <div>
                  <h2 className="m-0 mb-[0.25rem] text-m font-bold leading-[1.5rem] m600:mb-[0.5rem]">
                    Over £3 billion across 50,000 small businesses
                  </h2>
                  <p className="m-0 text-s font-reg leading-[1.25rem] m600:text-m m600:leading-[1.5rem]">
                    We’re backed by iwoca, one of the largest independent
                    Business Funding companies in the UK.
                  </p>
                </div>
              </li>
              <li className="mb-[0.75rem] flex m600:mb-[1.5rem]">
                <span className="mr-[0.75rem] hidden overflow-visible m600:block">
                  <img src={BenefitPencil} alt="" />
                </span>
                <div>
                  <h2 className="m-0 mb-[0.25rem] text-m font-bold leading-[1.5rem] m600:mb-[0.5rem]">
                    Never fill in a form again
                  </h2>
                  <p className="m-0 text-s font-reg leading-[1.25rem] m600:text-m m600:leading-[1.5rem]">
                    Do it once, and we’ll remember your details for next time
                    you use your iwocaPay account.
                  </p>
                </div>
              </li>
            </ul>
            <WhiteLabelOverride componentType="button" disabled={false}>
              <LinkWithQuery
                className="mb-[0.75rem] block w-full rounded-s bg-cta-button py-[0.75rem] text-center text-[1.125rem] font-bold leading-[1.75rem] text-white no-underline shadow-cta-button hover:bg-denim-35"
                to={`/pay/order/${orderId}/`}
              >
                Head to checkout
              </LinkWithQuery>
            </WhiteLabelOverride>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderLandingPage;
